import { useEffect, useState } from "react";
import { fmtDate, api, errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, Rag, Status } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { PartiesPanel } from "./PartiesPanel";

export default function OverviewTab({ c, reload }) {
  const { can, config } = useAuth();
  const [next, setNext] = useState("");
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState(c.member_id || "");
  const comp = c.compliance;
  useEffect(() => { if (can("case.edit")) api.get("/auth/users").then((r) => setMembers(r.data.filter((u) => u.role === "member" && u.active !== false))).catch(() => {}); }, []); // eslint-disable-line
  const advance = async () => {
    try { await api.patch(`/cases/${c.id}/status`, { status: next }); toast.success("Stage updated"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const allocate = async () => {
    try { await api.patch(`/cases/${c.id}/member`, { member_id: memberId || null }); toast.success(memberId ? "Member allocated and notified" : "Allocation removed"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid md:grid-cols-12 gap-6 mt-4">
      <div className="md:col-span-8 space-y-6">
        <Panel title="AI recommendation (rules-based)" testId="overview-recommendation">
          <div className="flex items-start gap-3">
            <Rag level={c.triage} label={c.triage.toUpperCase()} />
            <div><div className="text-sm text-slate-900 font-medium">{c.ai_recommendation.text}</div>
              <div className="eyebrow mt-1">basis: {c.ai_recommendation.basis} · engine {c.ai_recommendation.engine_version} · rule "{c.ai_recommendation.rule}"</div></div>
          </div>
        </Panel>
        <Panel title="Matter" testId="overview-matter">
          <p className="text-sm text-slate-700 leading-relaxed">{c.description}</p>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 mt-4 text-sm">
            <div><dt className="eyebrow">{config?.terminology?.applicant}</dt><dd className="font-medium">{c.applicant.name}</dd><dd className="text-slate-500 text-xs">{c.applicant.representative || "Self-represented"}</dd></div>
            <div><dt className="eyebrow">{config?.terminology?.respondent}</dt><dd className="font-medium">{c.respondent.name}</dd><dd className="text-slate-500 text-xs">{c.respondent.representative || "Self-represented"}</dd></div>
            <div><dt className="eyebrow">Claim</dt><dd className="mono">{money(c.claim_amount)}</dd></div>
            <div><dt className="eyebrow">Counterclaim</dt><dd className="mono">{money(c.counterclaim_amount)}</dd></div>
            <div><dt className="eyebrow">Filed</dt><dd>{fmtDate(c.filed_at)}</dd></div>
            <div><dt className="eyebrow">Presiding {config?.terminology?.member}</dt><dd data-testid="case-member-name">{c.member_name || "Unallocated"}</dd>
              {can("case.edit") && <dd className="flex gap-1 mt-1"><select className="h-7 text-xs border rounded px-1" value={memberId} onChange={(e) => setMemberId(e.target.value)} data-testid="allocate-member-select"><option value="">Unallocated</option>{members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}</select>
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={memberId === (c.member_id || "")} onClick={allocate} data-testid="allocate-member-btn">Allocate</Button></dd>}</div>
          </dl>
        </Panel>
        <Panel title="Workflow stage" testId="overview-workflow" action={can("case.edit") && (
          <div className="flex items-center gap-2">
            <Select value={next} onValueChange={setNext}><SelectTrigger className="h-8 w-56" data-testid="stage-select"><SelectValue placeholder="Move to stage…" /></SelectTrigger>
              <SelectContent>{config?.workflow_states.map((s) => <SelectItem key={s.key} value={s.key} data-testid={`stage-option-${s.key}`}>{s.label}</SelectItem>)}</SelectContent></Select>
            <Button size="sm" className="bg-blue-900" disabled={!next} onClick={advance} data-testid="stage-advance-btn">Update</Button>
          </div>)}>
          <ol className="flex flex-wrap gap-2">
            {config?.workflow_states.map((s) => {
              const cur = config.workflow_states.find((x) => x.key === c.status)?.order || 0;
              const cls = s.order < cur ? "bg-emerald-50 border-emerald-200 text-emerald-800" : s.order === cur ? "bg-blue-900 border-blue-900 text-white" : "bg-white border-slate-200 text-slate-500";
              return <li key={s.key} className={`text-xs border rounded px-2.5 py-1 ${cls}`} data-testid={`stage-${s.key}`}>{s.order}. {s.label}</li>;
            })}
          </ol>
        </Panel>
      </div>
      <div className="md:col-span-4 space-y-6">
        <PartiesPanel c={c} />
        <Panel title="Overdue items" testId="overview-overdue">
          {c.overdue_items.length === 0 ? <div className="text-sm text-emerald-700">No overdue items</div> : c.overdue_items.map((o) => (
            <div key={o.rule_key} className="flex items-start justify-between gap-2 py-2 border-b border-slate-100 last:border-0">
              <div><div className="text-sm font-medium">{o.label}</div><div className="text-xs text-slate-500">{o.party} · due {fmtDate(o.due_date)}</div></div>
              <span className="text-xs text-rose-700 font-semibold mono">{o.days_overdue}d</span>
            </div>))}
        </Panel>
        <Panel title="Next event" testId="overview-next-event">
          {c.next_event ? <><div className="font-medium">{c.next_event.title}</div><div className="text-sm text-slate-600">{fmtDate(c.next_event.date)}</div><div className="text-xs text-slate-500 mt-1">{c.next_event.notes}</div></> : <div className="text-sm text-slate-500">Nothing listed</div>}
        </Panel>
        <Panel title="Compliance snapshot" testId="overview-compliance">
          <ul className="space-y-1.5">{comp.rules.filter((r) => r.status !== "not_yet_required").map((r) => (
            <li key={r.rule_key} className="flex items-center justify-between gap-2 text-sm"><span className="truncate">{r.label}</span><Status value={r.status} /></li>))}</ul>
        </Panel>
      </div>
    </div>
  );
}
